import React, { useEffect, useState, useCallback } from "react";
import { Download, Loader2, Mail, MessageCircle, Search, ShieldCheck, Users } from "lucide-react";
import { api } from "@/lib/api";

/**
 * AdminLeads, Audiência → Leads Mensagens Diárias.
 * Lista com filtros (canal, status, busca), toggle de status e export CSV.
 */
const STATUS_LABEL = { active: "Ativo", inactive: "Inativo", blocked: "Bloqueado" };
const CH_LABEL = { email: "E-mail", whatsapp: "WhatsApp", both: "Ambos" };

export default function AdminLeads() {
  const [data, setData] = useState({ total: 0, items: [], stats: null });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ channel: "all", status: "all", q: "" });
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.channel !== "all") params.set("channel", filters.channel);
    if (filters.status !== "all") params.set("status", filters.status);
    if (filters.q) params.set("q", filters.q);
    api
      .get(`/admin/leads?${params}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ total: 0, items: [], stats: null }))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const toggle = async (lead) => {
    setBusyId(lead.id);
    const next = lead.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/admin/leads/${lead.id}`, { status: next });
      load();
    } finally {
      setBusyId(null);
    }
  };

  const exportCSV = () => {
    const params = new URLSearchParams();
    if (filters.channel !== "all") params.set("channel", filters.channel);
    if (filters.status !== "all") params.set("status", filters.status);
    const token = localStorage.getItem("token") || "";
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    fetch(`${apiBase}/api/admin/leads/export?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url;
        a.download = `leads_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
  };

  const stats = data.stats || { by_channel: {}, by_status: {}, grand_total: 0 };

  return (
    <section
      data-testid="admin-leads-section"
      className="mt-14 bg-white border border-[#0B1E36]/10 p-8"
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[#C5A059] mb-1">
            <Users size={12} strokeWidth={1.6} />
            <span className="text-[0.6rem] uppercase tracking-[0.28em] font-medium">
              Audiência
            </span>
          </div>
          <h2 className="font-serif-display text-[1.4rem] text-[#0B1E36] tracking-[-0.01em]">
            Leads · Mensagens diárias
          </h2>
        </div>
        <button
          data-testid="admin-leads-export"
          onClick={exportCSV}
          className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36] bg-[#C5A059] hover:bg-[#D4B273] font-medium px-4 py-2.5"
        >
          <Download size={13} strokeWidth={1.7} />
          Exportar CSV
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Total" value={stats.grand_total} />
        <Stat label="E-mail" value={stats.by_channel.email || 0} icon={Mail} />
        <Stat label="WhatsApp" value={stats.by_channel.whatsapp || 0} icon={MessageCircle} />
        <Stat label="Ambos" value={stats.by_channel.both || 0} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 bg-[#FAF8F4] border border-[#0B1E36]/8 p-3 mb-4">
        <SearchInput
          value={filters.q}
          onChange={(v) => setFilters((f) => ({ ...f, q: v }))}
        />
        <SelectChip
          label="Canal"
          value={filters.channel}
          onChange={(v) => setFilters((f) => ({ ...f, channel: v }))}
          options={[
            { v: "all", label: "Todos" },
            { v: "email", label: "E-mail" },
            { v: "whatsapp", label: "WhatsApp" },
            { v: "both", label: "Ambos" },
          ]}
        />
        <SelectChip
          label="Status"
          value={filters.status}
          onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
          options={[
            { v: "all", label: "Todos" },
            { v: "active", label: "Ativos" },
            { v: "inactive", label: "Inativos" },
            { v: "blocked", label: "Bloqueados" },
          ]}
        />
      </div>

      {/* Table */}
      <div className="border border-[#0B1E36]/10 overflow-x-auto">
        <table className="w-full text-sm text-[#0B1E36]">
          <thead className="bg-[#FAF8F4] text-[0.6rem] uppercase tracking-[0.22em] text-[#0B1E36]/55">
            <tr>
              <th className="text-left px-3 py-2.5">Nome</th>
              <th className="text-left px-3 py-2.5">E-mail</th>
              <th className="text-left px-3 py-2.5">Telefone</th>
              <th className="text-left px-3 py-2.5">Canal</th>
              <th className="text-left px-3 py-2.5">Origem</th>
              <th className="text-left px-3 py-2.5">Status</th>
              <th className="text-left px-3 py-2.5">Inscrito em</th>
              <th className="text-right px-3 py-2.5">Ação</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-10 text-[#0B1E36]/50">
                <Loader2 size={16} className="inline animate-spin mr-2" /> Carregando…
              </td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-10 text-[#0B1E36]/45 italic">
                Nenhum lead encontrado.
              </td></tr>
            ) : (
              data.items.map((l) => (
                <tr key={l.id} data-testid={`admin-lead-row-${l.id}`} className="border-t border-[#0B1E36]/8 hover:bg-[#FAF8F4]/60">
                  <td className="px-3 py-2.5">{l.name}</td>
                  <td className="px-3 py-2.5 text-[#0B1E36]/70 tabular-nums">{l.email || "·"}</td>
                  <td className="px-3 py-2.5 text-[#0B1E36]/70 tabular-nums">{l.phone || "·"}</td>
                  <td className="px-3 py-2.5">
                    <span className="text-[0.62rem] uppercase tracking-[0.2em] text-[#C5A059] font-medium">{CH_LABEL[l.channel]}</span>
                  </td>
                  <td className="px-3 py-2.5 text-[#0B1E36]/55 text-[0.78rem]">{l.source || "·"}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[0.62rem] uppercase tracking-[0.2em] font-medium ${
                      l.status === "active" ? "text-emerald-700" :
                      l.status === "blocked" ? "text-rose-700" : "text-[#0B1E36]/40"
                    }`}>{STATUS_LABEL[l.status] || l.status}</span>
                  </td>
                  <td className="px-3 py-2.5 text-[#0B1E36]/50 text-[0.78rem] tabular-nums">
                    {l.created_at ? new Date(l.created_at).toLocaleDateString("pt-BR") : "·"}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button
                      data-testid={`admin-lead-toggle-${l.id}`}
                      disabled={busyId === l.id}
                      onClick={() => toggle(l)}
                      className="text-[0.62rem] uppercase tracking-[0.18em] text-[#0B1E36]/65 hover:text-[#C5A059] border-b border-transparent hover:border-[#C5A059] pb-0.5"
                    >
                      {busyId === l.id ? "…" : l.status === "active" ? "Inativar" : "Ativar"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-[0.62rem] uppercase tracking-[0.18em] text-[#0B1E36]/45 flex items-center gap-2">
        <ShieldCheck size={11} strokeWidth={1.6} className="text-emerald-700/80" />
        LGPD, todos os leads acima deram consentimento explícito · IP não armazenado
      </p>
    </section>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="border border-[#0B1E36]/10 bg-[#FAF8F4] p-3">
      <div className="flex items-center gap-2 text-[#C5A059] mb-1">
        {Icon && <Icon size={11} strokeWidth={1.6} />}
        <span className="text-[0.55rem] uppercase tracking-[0.22em]">{label}</span>
      </div>
      <p className="text-[1.4rem] text-[#0B1E36] tabular-nums font-light leading-none">{value}</p>
    </div>
  );
}

function SearchInput({ value, onChange }) {
  return (
    <div className="relative flex-1 min-w-[180px]">
      <Search size={14} strokeWidth={1.6} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0B1E36]/40" />
      <input
        data-testid="admin-leads-search"
        value={value}
        placeholder="Buscar por nome, e-mail, telefone…"
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-[#0B1E36]/15 focus:border-[#C5A059] outline-none pl-9 pr-3 py-2 text-sm text-[#0B1E36] placeholder:text-[#0B1E36]/35"
      />
    </div>
  );
}

function SelectChip({ label, value, onChange, options }) {
  return (
    <label className="flex items-center gap-2 text-xs">
      <span className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0B1E36]/55">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-white border border-[#0B1E36]/15 text-[#0B1E36] text-xs px-2.5 py-2 focus:border-[#C5A059] outline-none"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
